import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const ParametrosGlobales = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [parametros, setParametros] = useState({
        iva_defecto: '',
        retencion_fuente: '',
        ica: '',
        prefijo_factura: '',
        numeracion_inicial: '',
        dias_vencimiento: '',
        tiempo_sesion: '',
        tamano_max_archivos: '',
        formato_fecha: 'Y-m-d',
        email_soporte: '',
        email_notificaciones: '',
        habilitar_email: true
    });
    const [historial, setHistorial] = useState([]);
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [showHistorial, setShowHistorial] = useState(false);
    const [filtroParametro, setFiltroParametro] = useState('todos');
    const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
    const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchParametros();
        fetchHistorial();
    }, [history]);

    const fetchParametros = async () => {
        try {
            const response = await axios.get('/superadmin/parametros');
            const params = response.data;
            
            const formattedParams = {};
            params.forEach(param => {
                formattedParams[param.clave] = param.valor;
            });

            setParametros({
                iva_defecto: formattedParams.iva_defecto || '',
                retencion_fuente: formattedParams.retencion_fuente || '',
                ica: formattedParams.ica || '',
                prefijo_factura: formattedParams.prefijo_factura || '',
                numeracion_inicial: formattedParams.numeracion_inicial || '',
                dias_vencimiento: formattedParams.dias_vencimiento || '',
                tiempo_sesion: formattedParams.tiempo_sesion || '',
                tamano_max_archivos: formattedParams.tamano_max_archivos || '',
                formato_fecha: formattedParams.formato_fecha || 'Y-m-d',
                email_soporte: formattedParams.email_soporte || '',
                email_notificaciones: formattedParams.email_notificaciones || '',
                habilitar_email: formattedParams.habilitar_email === 'true' || formattedParams.habilitar_email === true
            });
            setLoading(false);
        } catch (error) {
            console.error('Error fetching parametros:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
            setLoading(false);
        }
    };

    const fetchHistorial = async () => {
        try {
            const response = await axios.get('/superadmin/parametros/historial');
            setHistorial(response.data);
        } catch (error) {
            console.error('Error fetching historial:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setParametros({
            ...parametros,
            [name]: type === 'checkbox' ? checked : value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        const porcentajes = ['iva_defecto', 'retencion_fuente', 'ica'];
        porcentajes.forEach(campo => {
            if (parametros[campo] !== '' && (parseFloat(parametros[campo]) < 0 || parseFloat(parametros[campo]) > 100)) {
                newErrors[campo] = 'El porcentaje debe estar entre 0 y 100';
            }
        });

        const numerosPositivos = ['numeracion_inicial', 'dias_vencimiento', 'tiempo_sesion', 'tamano_max_archivos'];
        numerosPositivos.forEach(campo => {
            if (parametros[campo] !== '' && parseFloat(parametros[campo]) < 0) {
                newErrors[campo] = 'Debe ser un número positivo';
            }
        });

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (parametros.email_soporte && !emailRegex.test(parametros.email_soporte)) {
            newErrors.email_soporte = 'Email inválido';
        }
        if (parametros.email_notificaciones && !emailRegex.test(parametros.email_notificaciones)) {
            newErrors.email_notificaciones = 'Email inválido';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            await axios.put('/superadmin/parametros', parametros);
            setAlert({
                show: true,
                type: 'success',
                message: 'Parámetros actualizados exitosamente'
            });
            fetchHistorial();
        } catch (error) {
            console.error('Error updating parametros:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al actualizar los parámetros'
            });
        } finally {
            setSubmitting(false);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const getFilteredHistorial = () => {
        let filtered = historial;

        if (filtroParametro !== 'todos') {
            filtered = filtered.filter(h => h.clave === filtroParametro);
        }

        if (filtroFechaDesde) {
            filtered = filtered.filter(h => new Date(h.created_at) >= new Date(filtroFechaDesde));
        }

        if (filtroFechaHasta) {
            filtered = filtered.filter(h => new Date(h.created_at) <= new Date(filtroFechaHasta));
        }

        return filtered.slice(0, 50);
    };

    const getParametrosUnicos = () => {
        const claves = historial.map(h => h.clave);
        return [...new Set(claves)];
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4><i className="fas fa-cog"></i> Parámetros Globales del Sistema</h4>
                <button
                    className="btn btn-outline-dark"
                    onClick={() => setShowHistorial(!showHistorial)}
                >
                    <i className="fas fa-history"></i> {showHistorial ? 'Ocultar' : 'Ver'} Historial
                </button>
            </div>

            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h5><i className="fas fa-percentage"></i> Impuestos</h5>
                    
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="iva_defecto">IVA por Defecto (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className={`form-control ${errors.iva_defecto ? 'is-invalid' : ''}`}
                                    id="iva_defecto"
                                    name="iva_defecto"
                                    value={parametros.iva_defecto}
                                    onChange={handleChange}
                                    placeholder="19"
                                />
                                {errors.iva_defecto && (
                                    <div className="invalid-feedback">{errors.iva_defecto}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="retencion_fuente">Retención en la Fuente (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className={`form-control ${errors.retencion_fuente ? 'is-invalid' : ''}`}
                                    id="retencion_fuente"
                                    name="retencion_fuente"
                                    value={parametros.retencion_fuente}
                                    onChange={handleChange}
                                    placeholder="2.5"
                                />
                                {errors.retencion_fuente && (
                                    <div className="invalid-feedback">{errors.retencion_fuente}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="ica">ICA (%)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className={`form-control ${errors.ica ? 'is-invalid' : ''}`}
                                    id="ica"
                                    name="ica"
                                    value={parametros.ica}
                                    onChange={handleChange}
                                    placeholder="0.966"
                                />
                                {errors.ica && (
                                    <div className="invalid-feedback">{errors.ica}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5><i className="fas fa-file-invoice"></i> Facturación</h5>
                    
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="prefijo_factura">Prefijo de Factura</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="prefijo_factura"
                                    name="prefijo_factura"
                                    value={parametros.prefijo_factura}
                                    onChange={handleChange}
                                    placeholder="FAC"
                                />
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="numeracion_inicial">Numeración Inicial</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.numeracion_inicial ? 'is-invalid' : ''}`}
                                    id="numeracion_inicial"
                                    name="numeracion_inicial"
                                    value={parametros.numeracion_inicial}
                                    onChange={handleChange}
                                    placeholder="1"
                                />
                                {errors.numeracion_inicial && (
                                    <div className="invalid-feedback">{errors.numeracion_inicial}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="dias_vencimiento">Días de Vencimiento</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.dias_vencimiento ? 'is-invalid' : ''}`}
                                    id="dias_vencimiento"
                                    name="dias_vencimiento"
                                    value={parametros.dias_vencimiento}
                                    onChange={handleChange}
                                    placeholder="30"
                                />
                                {errors.dias_vencimiento && (
                                    <div className="invalid-feedback">{errors.dias_vencimiento}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5><i className="fas fa-server"></i> Sistema</h5>
                    
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="tiempo_sesion">Tiempo de Sesión (minutos)</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.tiempo_sesion ? 'is-invalid' : ''}`}
                                    id="tiempo_sesion"
                                    name="tiempo_sesion"
                                    value={parametros.tiempo_sesion}
                                    onChange={handleChange}
                                    placeholder="120"
                                />
                                {errors.tiempo_sesion && (
                                    <div className="invalid-feedback">{errors.tiempo_sesion}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="tamano_max_archivos">Tamaño Máx. Archivos (MB)</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.tamano_max_archivos ? 'is-invalid' : ''}`}
                                    id="tamano_max_archivos"
                                    name="tamano_max_archivos"
                                    value={parametros.tamano_max_archivos}
                                    onChange={handleChange}
                                    placeholder="10"
                                />
                                {errors.tamano_max_archivos && (
                                    <div className="invalid-feedback">{errors.tamano_max_archivos}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="formato_fecha">Formato de Fecha</label>
                                <select
                                    className="form-control"
                                    id="formato_fecha"
                                    name="formato_fecha"
                                    value={parametros.formato_fecha}
                                    onChange={handleChange}
                                >
                                    <option value="Y-m-d">YYYY-MM-DD</option>
                                    <option value="d/m/Y">DD/MM/YYYY</option>
                                    <option value="m/d/Y">MM/DD/YYYY</option>
                                    <option value="d-m-Y">DD-MM-YYYY</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5><i className="fas fa-envelope"></i> Notificaciones</h5>
                    
                    <div className="row">
                        <div className="col-md-5">
                            <div className="form-group">
                                <label htmlFor="email_soporte">Email de Soporte</label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email_soporte ? 'is-invalid' : ''}`}
                                    id="email_soporte"
                                    name="email_soporte"
                                    value={parametros.email_soporte}
                                    onChange={handleChange}
                                    placeholder="soporte@ejemplo.com"
                                />
                                {errors.email_soporte && (
                                    <div className="invalid-feedback">{errors.email_soporte}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-5">
                            <div className="form-group">
                                <label htmlFor="email_notificaciones">Email de Notificaciones</label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email_notificaciones ? 'is-invalid' : ''}`}
                                    id="email_notificaciones"
                                    name="email_notificaciones"
                                    value={parametros.email_notificaciones}
                                    onChange={handleChange}
                                    placeholder="notificaciones@ejemplo.com"
                                />
                                {errors.email_notificaciones && (
                                    <div className="invalid-feedback">{errors.email_notificaciones}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-2">
                            <div className="form-group">
                                <label>&nbsp;</label>
                                <div className="custom-control custom-checkbox">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="habilitar_email"
                                        name="habilitar_email"
                                        checked={parametros.habilitar_email}
                                        onChange={handleChange}
                                    />
                                    <label className="custom-control-label" htmlFor="habilitar_email">
                                        Habilitar Email
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-group">
                    <button
                        type="submit"
                        className="btn btn-superadmin"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i> Guardar Parámetros
                            </>
                        )}
                    </button>
                </div>
            </form>

            {showHistorial && (
                <div className="card mt-4">
                    <div className="card-header">
                        <h5 className="mb-0"><i className="fas fa-history"></i> Historial de Cambios</h5>
                    </div>
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label>Parámetro</label>
                                <select
                                    className="form-control"
                                    value={filtroParametro}
                                    onChange={(e) => setFiltroParametro(e.target.value)}
                                >
                                    <option value="todos">Todos</option>
                                    {getParametrosUnicos().map(param => (
                                        <option key={param} value={param}>{param}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label>Desde</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filtroFechaDesde}
                                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <label>Hasta</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filtroFechaHasta}
                                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Parámetro</th>
                                        <th>Valor Anterior</th>
                                        <th>Valor Nuevo</th>
                                        <th>Usuario</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getFilteredHistorial().length > 0 ? (
                                        getFilteredHistorial().map((item, index) => (
                                            <tr key={index}>
                                                <td>{new Date(item.created_at).toLocaleString()}</td>
                                                <td>{item.clave}</td>
                                                <td>{item.valor_anterior || '-'}</td>
                                                <td>{item.valor_nuevo}</td>
                                                <td>{item.usuario_nombre || 'Sistema'}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted">
                                                No hay registros en el historial
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </SuperadminLayout>
    );
};

export default ParametrosGlobales;
